import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ColumnConfig, ColumnPinSide } from './trades-column-store';

export type { ColumnConfig, ColumnPinSide } from './trades-column-store';

// Synthetic column ids for the MTM price-editing block (Last Marked | New Price |
// Gross Price Δ | New Margin Value | Gross Value Δ | Margin Value Δ). These do not
// map to a TradeItem field — MtmTable renders them from per-row helpers — but they
// live in the column store so show/hide, reorder and pin work on them exactly like
// real columns.
// NOTE: the delta ids are frozen even though their labels have moved twice — the
// `merge` below refreshes labels from the defaults, so a rename never costs a reset.
// `mtm_delta` predates the LR-433 split (Δ → Price Δ); LR-538 then took the trio to
// the gross/margin naming the rest of the grid uses (LR-522): Price Δ → Gross Price Δ,
// Value Δ → Gross Value Δ, Collateral Δ → Margin Value Δ.
export const MTM_SYNTHETIC_LAST_MARKED = 'mtm_last_marked';
export const MTM_SYNTHETIC_NEW_PRICE = 'mtm_new_price';
export const MTM_SYNTHETIC_DELTA = 'mtm_delta';
export const MTM_SYNTHETIC_NEW_MARGIN = 'mtm_new_margin';
export const MTM_SYNTHETIC_VALUE_DELTA = 'mtm_value_delta';
// LR-461: margined value delta — Gross Value Δ scaled by the position's margin.
// Mirrors the History "Margin Value Δ" column so the desk sees the margin-call impact
// on the preview, not just the gross market-value delta.
export const MTM_SYNTHETIC_COLLATERAL_DELTA = 'mtm_collateral_delta';

export const MTM_SYNTHETIC_PRICE_IDS: readonly string[] = [
  MTM_SYNTHETIC_LAST_MARKED,
  MTM_SYNTHETIC_NEW_PRICE,
  MTM_SYNTHETIC_DELTA,
  MTM_SYNTHETIC_NEW_MARGIN,
  MTM_SYNTHETIC_VALUE_DELTA,
  MTM_SYNTHETIC_COLLATERAL_DELTA,
];

type MtmColumnStore = {
  columns: ColumnConfig[];
  pinnedLeft: string[];
  pinnedRight: string[];
  setColumns: (columns: ColumnConfig[]) => void;
  toggleColumnVisibility: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  resetColumns: () => void;
  isColumnsDefault: () => boolean;
};

// Default MTM column order. Mirrors the curated MTM_COLUMN_ORDER from MtmTable:
// the synthetic price block (4 ids above) is injected right after the COB / current
// market price references, so the mark "workbench" stays contiguous on the left.
// Everything visible by default; no pins. Ids match createMtmColumns accessor keys.
const defaultColumns: ColumnConfig[] = [
  // Zone 1 — identify
  { id: 'id', label: 'ID', visible: true },
  { id: 'status_display', label: 'Status', visible: true },
  { id: 'symbol_id', label: 'Symbol', visible: true },
  { id: 'trade_type_display', label: 'Trade Type', visible: true },
  // Zone 2 — workbench: size + price references
  { id: 'quantity', label: 'Quantity', visible: true },
  // LR-457/522: Trade Gross Price = booking gross_price, shown next to the COB T-1
  // reference so the desk sees the original contract price beside the mark. LR-522
  // un-gated the def (it now also shows on the Trades blotter); label matches there.
  { id: 'gross_price', label: 'Trade Gross Price', visible: true },
  { id: 'cob_price', label: 'COB Gross Price', visible: true }, // LR-521: was 'COB Price (T-1)'
  // LR-494: COB (T-1 close) FX rate, security → settlement; sits beside COB
  // Price in the mark workbench. Cross-ccy rows only ("—" otherwise). The
  // smart `merge` below positions it next to its sibling for returning users,
  // so no version bump is needed for this addition.
  { id: 'cob_fx_rate', label: 'COB FX Rate', visible: true },
  { id: 'current_market_price', label: 'Current Market Price', visible: true },
  // Zone 2 — workbench: synthetic price block
  { id: MTM_SYNTHETIC_LAST_MARKED, label: 'Last Marked', visible: true },
  { id: MTM_SYNTHETIC_NEW_PRICE, label: 'New Price', visible: true },
  { id: MTM_SYNTHETIC_DELTA, label: 'Gross Price Δ', visible: true },
  { id: MTM_SYNTHETIC_NEW_MARGIN, label: 'New Margin Value', visible: true },
  { id: MTM_SYNTHETIC_VALUE_DELTA, label: 'Gross Value Δ', visible: true },
  { id: MTM_SYNTHETIC_COLLATERAL_DELTA, label: 'Margin Value Δ', visible: true },
  // Zone 2 — workbench: margin / value outputs. LR-538 brings the LR-522 value
  // taxonomy (Trade / Last Marked × Gross / Margin) onto the MtM grid — the column
  // defs already existed in createTradesColumns and were reachable only from the
  // blotter, because buildMtmColumnDefs gates on this default set. Labels match the
  // blotter exactly, so the same figure is never named two different things.
  { id: 'margin', label: 'Margin %', visible: true },
  { id: 'margin_price', label: 'Trade Margin Price', visible: true },
  { id: 'trade_gross_value', label: 'Trade Gross Value', visible: true },
  { id: 'contract_value', label: 'Trade Margin Value', visible: true },
  // Blank until the position is first marked (LR-522 made the marked twins
  // null-when-unmarked), and always blank for repo — repos are never MtM-repriced.
  { id: 'last_marked_gross_value', label: 'Last Marked Gross Value', visible: true },
  { id: 'marked_contract_value', label: 'Last Marked Margin Value', visible: true },
  // Zone 3a — mark consequences
  { id: 'closing_cash', label: 'Closing Cash', visible: true },
  { id: 'daily_earnings', label: 'Daily Earnings', visible: true },
  // Zone 3b — collateral / margin adequacy
  { id: 'collateral_type', label: 'Collateral Type', visible: true },
  { id: 'collateral_value', label: 'Collateral Value', visible: true },
  { id: 'collateral_isin', label: 'Collateral ISIN', visible: true },
  { id: 'collateral_quantity', label: 'Collateral Qty', visible: true },
  { id: 'pre_delivery_party', label: 'Pre-Delivery', visible: true },
  // Zone 3c — lifecycle & dates
  { id: 'maturity_date', label: 'Maturity Date', visible: true },
  { id: 'trade_date', label: 'Trade Date', visible: true },
  { id: 'settlement_date', label: 'Settlement Date', visible: true },
  { id: 'term_type_display', label: 'Trade Term', visible: true },
  // Zone 3d — security detail & classification
  { id: 'description', label: 'Description', visible: true },
  { id: 'isin', label: 'ISIN', visible: true },
  { id: 'agreement_display', label: 'Agreement', visible: true },
  // Zone 3e — financing economics
  { id: 'rate_entry_mode', label: 'Rate Mode', visible: true },
  { id: 'benchmark_name', label: 'Benchmark', visible: true },
  { id: 'total_rate', label: 'Total Rate', visible: true },
  { id: 'spread_bps', label: 'Spread (bps)', visible: true },
  { id: 'fee_percent', label: 'Fee %', visible: true },
  { id: 'rate_label', label: 'Rate Type', visible: true },
  { id: 'cost_of_collateral', label: 'Cash Pool Interest Accrual', visible: true },
  { id: 'fee_income', label: 'Fee Income', visible: true },
  { id: 'net_amount', label: 'Net Amount', visible: true },
  // Zone 3f — settlement & accounts
  { id: 'delivery_type', label: 'Delivery Type', visible: true },
  { id: 'settlement_venue', label: 'Settlement Venue', visible: true },
  { id: 'booking_center', label: 'Booking Center', visible: true },
  { id: 'custodian_name', label: 'Custodian', visible: true },
  { id: 'custodian_account', label: 'Custodian Account', visible: true },
  { id: 'counterparty_account', label: 'Counterparty Account', visible: true },
  // Zone 3g — units & audit
  // LR-474: 'Currency' → 'Security Ccy' (label feeds column menu + CSV header);
  // id unchanged. Settlement Ccy lands next to it via the sibling-splice merge.
  { id: 'currency', label: 'Security Ccy', visible: true },
  { id: 'settlement_currency', label: 'Settlement Ccy', visible: true },
  { id: 'created_at', label: 'Created At', visible: true },
  { id: 'updated_at', label: 'Updated At', visible: true },
];

// Exported for tests/consumers that need the curated default order without
// reaching into the store (e.g. building column defs in a mocked harness).
export const MTM_DEFAULT_COLUMNS: ColumnConfig[] = defaultColumns;

const DEFAULT_PINNED_LEFT: string[] = [];
const DEFAULT_PINNED_RIGHT: string[] = [];

export const useMtmColumnStore = create<MtmColumnStore>()(
  devtools(
    persist(
      (set, get) => ({
        columns: defaultColumns,
        pinnedLeft: DEFAULT_PINNED_LEFT,
        pinnedRight: DEFAULT_PINNED_RIGHT,
        setColumns: (columns: ColumnConfig[]) => set({ columns }),
        toggleColumnVisibility: (columnId: string) =>
          set((state: MtmColumnStore) => {
            const col = state.columns.find((c) => c.id === columnId);
            const becomingHidden = col?.visible === true;
            return {
              columns: state.columns.map((c: ColumnConfig) =>
                c.id === columnId ? { ...c, visible: !c.visible } : c
              ),
              // Auto-unpin when hiding
              pinnedLeft: becomingHidden
                ? state.pinnedLeft.filter((id) => id !== columnId)
                : state.pinnedLeft,
              pinnedRight: becomingHidden
                ? state.pinnedRight.filter((id) => id !== columnId)
                : state.pinnedRight,
            };
          }),
        showAllColumns: () =>
          set((state: MtmColumnStore) => ({
            columns: state.columns.map((c) => ({ ...c, visible: true })),
          })),
        hideAllColumns: () =>
          set((state: MtmColumnStore) => ({
            columns: state.columns.map((c) => ({ ...c, visible: false })),
            pinnedLeft: [],
            pinnedRight: [],
          })),
        reorderColumns: (fromIndex: number, toIndex: number) =>
          set((state: MtmColumnStore) => {
            const newColumns = [...state.columns];
            const [movedColumn] = newColumns.splice(fromIndex, 1);
            newColumns.splice(toIndex, 0, movedColumn);
            return { columns: newColumns };
          }),
        pinColumn: (columnId: string, side: ColumnPinSide) =>
          set((state: MtmColumnStore) => {
            const newLeft = state.pinnedLeft.filter((id) => id !== columnId);
            const newRight = state.pinnedRight.filter((id) => id !== columnId);
            if (side === 'left') newLeft.push(columnId);
            if (side === 'right') newRight.push(columnId);
            return { pinnedLeft: newLeft, pinnedRight: newRight };
          }),
        getPinState: (columnId: string): ColumnPinSide => {
          const state = get();
          if (state.pinnedLeft.includes(columnId)) return 'left';
          if (state.pinnedRight.includes(columnId)) return 'right';
          return null;
        },
        resetColumns: () =>
          set({
            columns: defaultColumns,
            pinnedLeft: DEFAULT_PINNED_LEFT,
            pinnedRight: DEFAULT_PINNED_RIGHT,
          }),
        isColumnsDefault: (): boolean => {
          const state: MtmColumnStore = get();
          if (state.columns.length !== defaultColumns.length) return false;
          const leftMatch = state.pinnedLeft.length === DEFAULT_PINNED_LEFT.length &&
            state.pinnedLeft.every((id, i) => id === DEFAULT_PINNED_LEFT[i]);
          const rightMatch = state.pinnedRight.length === DEFAULT_PINNED_RIGHT.length &&
            state.pinnedRight.every((id, i) => id === DEFAULT_PINNED_RIGHT[i]);
          if (!leftMatch || !rightMatch) return false;
          return state.columns.every(
            (col: ColumnConfig, idx: number) =>
              col.id === defaultColumns[idx].id && col.visible === defaultColumns[idx].visible
          );
        },
      }),
      {
        name: 'mtm-columns-storage',
        // Column ADDITIONS (LR-433 `mtm_value_delta`, LR-461 `mtm_collateral_delta`,
        // LR-538 `trade_gross_value` / `last_marked_gross_value` / `marked_contract_value`)
        // and pure LABEL renames (LR-538's gross/margin delta names) are handled in
        // place by the smarter `merge` below — no schema break, so the version stays
        // put for those. Bumping the version WITHOUT a `migrate`
        // callback makes zustand persist DISCARD all prior layouts as non-migratable,
        // resetting every returning user — strictly worse than the tail-append UX.
        // LR-466 (v2): rename the `daily_earnings_usd` column id to `daily_earnings`.
        // Unlike an addition, a rename leaves a stale id in persisted state that
        // `merge` would drop (resetting the column's visibility to the default),
        // so this case DOES bump the version — but ships the `migrate` below that
        // renames the id in place, preserving the user's order + visibility.
        version: 2,
        migrate: (persistedState: unknown, version: number) => {
          if (!persistedState || typeof persistedState !== 'object') return persistedState;
          const state = persistedState as { columns?: ColumnConfig[] };
          if (version < 2 && Array.isArray(state.columns)) {
            const i = state.columns.findIndex((c) => c.id === 'daily_earnings_usd');
            if (i !== -1) {
              state.columns[i] = { ...state.columns[i], id: 'daily_earnings', label: 'Daily Earnings' };
            }
          }
          return persistedState;
        },
        // Reconcile persisted state against the current default set: keep persisted
        // order + visibility for ids that still exist, drop unknown ids, and splice
        // any new default columns into the position they sit at in `defaultColumns`
        // relative to their nearest already-persisted neighbour. This way a column
        // added in a later release lands next to its sibling for a returning user,
        // not stranded at the far right of the column list.
        merge: (persistedState: unknown, currentState: MtmColumnStore): MtmColumnStore => {
          const persisted =
            typeof persistedState === 'object' && persistedState !== null
              ? (persistedState as Partial<MtmColumnStore>)
              : undefined;
          if (!persisted?.columns?.length) return currentState;
          const persistedById = new Map(persisted.columns.map((c: ColumnConfig) => [c.id, c]));
          const defaultById = new Map(defaultColumns.map((c: ColumnConfig) => [c.id, c]));
          // Step 1 — keep persisted order + visibility for ids that still exist in
          // the current default set; refresh the label/metadata from default (so
          // renames like Δ → Price Δ → Gross Price Δ propagate without a reset).
          const mergedColumns: ColumnConfig[] = persisted.columns
            .filter((pc: ColumnConfig) => defaultById.has(pc.id))
            .map((pc: ColumnConfig) => ({ ...defaultById.get(pc.id)!, visible: pc.visible }));
          // Step 2 — splice in every default column that was NOT in persisted state.
          // Position rule: walk `defaultColumns` left-to-right from the new column's
          // index toward 0; the first default id that is present in `mergedColumns`
          // becomes the new column's predecessor. If none exists (new column is the
          // first default), insert at the head. This keeps new columns adjacent to
          // their default siblings for returning users, while preserving the user's
          // own reordering of pre-existing columns.
          for (let i = 0; i < defaultColumns.length; i++) {
            const dc = defaultColumns[i];
            if (persistedById.has(dc.id)) continue;
            let insertIdx = 0;
            for (let j = i - 1; j >= 0; j--) {
              const predIdx = mergedColumns.findIndex((m) => m.id === defaultColumns[j].id);
              if (predIdx !== -1) {
                insertIdx = predIdx + 1;
                break;
              }
            }
            mergedColumns.splice(insertIdx, 0, dc);
          }
          // Drop pins for ids that no longer exist in the default set (a column removed
          // in a later release), so a stale pin can never reference a missing column.
          const knownIds = new Set(mergedColumns.map((c) => c.id));
          const keepPins = (pins: unknown): string[] =>
            Array.isArray(pins) ? (pins as string[]).filter((id) => knownIds.has(id)) : [];
          return {
            ...currentState,
            ...persisted,
            columns: mergedColumns,
            pinnedLeft: keepPins(persisted.pinnedLeft),
            pinnedRight: keepPins(persisted.pinnedRight),
          };
        },
      }
    ),
    { name: 'mtm-column-store', enabled: import.meta.env.DEV }
  )
);
